import React, {useMemo} from 'react';
import {useTranslation} from 'next-i18next';
import type {StackProps} from '@tempo/core';
import {IconButton, Text, VStack} from '@tempo/core';
import {ClaimAccessType} from '@deezer/next/features/ClaimAccess/types';
import {
	AlbumIcon,
	HouseOutlinedIcon,
	DoubleNoteOutlinedIcon,
	HumanNoteIcon,
} from '@tempo/icons';

const claimTypes = {
	[ClaimAccessType.ARTIST]: {
		key: ClaimAccessType.ARTIST,
		Icon: DoubleNoteOutlinedIcon,
	},
	[ClaimAccessType.MANAGER]: {
		key: ClaimAccessType.MANAGER,
		Icon: HumanNoteIcon,
	},
	[ClaimAccessType.LABEL]: {
		key: ClaimAccessType.LABEL,
		Icon: HouseOutlinedIcon,
	},
	[ClaimAccessType.PROVIDER]: {
		key: ClaimAccessType.PROVIDER,
		Icon: AlbumIcon,
	},
};

type ClaimTypeButtonProps = {
	claimType: ClaimAccessType;
	isSelected: boolean;
	onClick: (claimType: ClaimAccessType) => void;
} & Omit<StackProps, 'onClick'>;

const ClaimTypeButton = ({
	claimType,
	isSelected,
	onClick,
	...stackProps
}: ClaimTypeButtonProps) => {
	const {t} = useTranslation('d4c');
	const {key, Icon} = claimTypes[claimType];

	const translatedLabel = useMemo(() => {
		switch (key) {
			case ClaimAccessType.ARTIST:
				return t('signuppage_text_anartist_d4c-web');
			case ClaimAccessType.MANAGER:
				return t('signuppage_text_amanager_d4c-web');
			case ClaimAccessType.LABEL:
				return t('signuppage_text_alabel_d4c-web');
			case ClaimAccessType.PROVIDER:
				return t('signuppage_text_aprovider_d4c-web');
		}
	}, [key, t]);

	const handleClick = () => {
		onClick(claimType);
	};

	return (
		<VStack spacing="spacing.xs" {...stackProps}>
			<IconButton
				aria-label={key}
				data-testid={`${claimType}-role-button`}
				variant={isSelected ? 'contain' : 'outline'}
				icon={<Icon />}
				onClick={handleClick}
			/>
			<Text variant="body.default.regular">{translatedLabel}</Text>
		</VStack>
	);
};

export default ClaimTypeButton;
